import { mergeStyles, Stack } from "@fluentui/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  getGridNumericOperators,
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridSortModel
} from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { FEATURE_REQUEST_COPY_TITLE, FEATURE_REQUEST_DATA_GRID_CONFIG } from "../components/FeatureRequestSearch/FeatureRequestSearch.constants";
import { getFeatureRequests } from "../Services/AzSearchServices";
import { callSearchAPIs, copy, getSearchInput } from "../Utils/js-common";
import { CustomDataGrid } from "./CustomDataGrid/CustomDataGrid";
import { IRows } from "./Interfaces/CustomDataGrid.interfaces";
import { CUSTOM_PARTNER_LIST_STYLES } from "./Styles/CustomPartnerList.style";
import { CustomLinkNavigate } from "./CustomLinkNavigate";
import { formatDate } from "../helper/DateFormat";

export function CustomFeatureRequestList({
  initialFilters,
  searchText,
  skipColumnsFilterable
}: Readonly<{
  initialFilters: string;
  searchText: any;
  skipColumnsFilterable?: string[];
}>) {
  const [title, setTitle] = useState(FEATURE_REQUEST_COPY_TITLE);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [rows, setRows] = useState<IRows>(FEATURE_REQUEST_DATA_GRID_CONFIG.rows);
  const [paginationOptions, setPaginationOptions] = useState(FEATURE_REQUEST_DATA_GRID_CONFIG.pagination);
  const [filterOptions, setFilterOptions] = useState<GridFilterModel>({
    items: []
  });
  const [sortOptions, setSortOptions] = useState<GridSortModel>([]);

  const columns: GridColDef[] = [
    {
      headerName: "Feature Request ID",
      field: "featureRequestId",
      minWidth: 150,
      sortable: true,
      renderCell: ({ row }: any) => (
        <Stack horizontal styles={{ root: CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idContainer }}>
          <Tooltip title={title} leaveDelay={500} arrow>
            <IconButton
              size="small"
              onClick={() => copyValue(row.featureRequestId, setTitle)}
              onMouseEnter={() => onMouseEnterTenantId(setTitle)}
              className={mergeStyles(CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipContainerBtn)}
            >
              <ContentCopyIcon sx={CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipCopyIconBtn} />
            </IconButton>
          </Tooltip>
          <CustomLinkNavigate to={navigateTo(row.featureRequestId)} text={row.featureRequestId} state={{ featureData: row }} />
        </Stack>
      )
    },
    {
      headerName: "Unified Insights ID",
      field: "unifiedFeatureRequestId",
      minWidth: 200,
      sortable: true,
      renderCell: ({ row }: any) => (
        <Stack horizontal styles={{ root: CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idContainer }}>
          <Tooltip title={title} leaveDelay={500} arrow>
            <IconButton
              size="small"
              onClick={() => copyValue(row.unifiedFeatureRequestId, setTitle)}
              onMouseEnter={() => onMouseEnterTenantId(setTitle)}
              className={mergeStyles(CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipContainerBtn)}
              disabled={!row.unifiedFeatureRequestId}
            >
              <ContentCopyIcon
                sx={{
                  ...CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipCopyIconBtn,
                  color: !row.unifiedFeatureRequestId ? "rgba(0,0,0,0.26)" : "inherit" // disabled color
                }}
              />
            </IconButton>
          </Tooltip>
          {row.unifiedFeatureRequestId}
        </Stack>
      )
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 100,
      sortable: true
    },
    {
      headerName: "Title",
      field: "title",
      minWidth: 250,
      sortable: true,
      renderCell: ({ row }: any) => <CustomLinkNavigate to={navigateTo(row.featureRequestId)} text={row.title} state={{ featureData: row }} />
    },
    {
      headerName: "Description",
      field: "description",
      minWidth: 250,
      sortable: false
    },
    {
      headerName: "Feature Category",
      field: "featureCategory",
      minWidth: 300,
      sortable: true
    },
    {
      headerName: "Product Name",
      field: "productName",
      minWidth: 200,
      sortable: true
    },
    {
      headerName: "Product Family",
      field: "productFamily",
      minWidth: 200,
      sortable: true
    },
    {
      headerName: "Customer Facing Status",
      field: "customerFacingStatus",
      minWidth: 188,
      sortable: true
    },
    {
      headerName: "Priority",
      field: "priority",
      minWidth: 188,
      sortable: true
    },
    {
      headerName: "Created Date",
      field: "createdDate",
      minWidth: 200,
      sortable: true,
      flex: 1,
      type: "date",
      valueGetter: (value, row) => new Date(row.createdDate),
      renderCell: (params) => formatDate(params.row.createdDate)
    },
    {
      headerName: "Created By",
      field: "createdBy",
      minWidth: 188,
      sortable: true
    },
    {
      headerName: "Feature Age In Days",
      field: "featureAgeInDays",
      minWidth: 188,
      sortable: true,
      flex: 1,
      type: "number",
    }
  ];

  useEffect(() => {
    const searchInput = getSearchInput(
      initialFilters,
      searchText,
      filterOptions,
      sortOptions,
      paginationOptions,
      "featureRequest"
    );
    callSearchAPIs(getFeatureRequests({ ...searchInput }), setLoading, setRows, setIsError);
  }, [initialFilters, paginationOptions, filterOptions, searchText, sortOptions]);

  return (
    <Box
      sx={{
        width: "calc(100% - 10px)",
        height: "calc(100% - 10px)",
        overflow: "auto"
      }}
    >
      <CustomDataGrid
        columns={columns.map((column) => getColumn(column, skipColumnsFilterable))}
        exportCsvButtonProps={{
          buttonLabel: "Export",
          fileNamePrefix: "feature-request-export",
          tooltip: "Export Feature Request List"
        }}
        filterMode="server"
        getRowId={(row) => row.featureRequestId}
        isError={isError}
        loading={loading}
        paginationMode="server"
        paginationOptions={paginationOptions}
        rowCount={rows.count}
        rows={rows}
        setFilterOptions={setFilterOptions}
        setPaginationOptions={setPaginationOptions}
        setSortOptions={setSortOptions}
        slotProps={{
          loadingOverlay: {
            variant: "circular-progress",
            noRowsVariant: "circular-progress"
          }
        }}
        sortingMode="server"
        toolbarConfig={{ disableGridToolbarQuickFilter: true }}
        showExploreDataButtonOnFooter={false}
      />
    </Box>
  );
}

function getColumn(col: GridColDef, skipColumnsFilterable?: string[]) {
  const stringOperators = getGridStringOperators().filter(
    ({ value }) => !["doesNotContain", "doesNotEqual", "isEmpty", "isNotEmpty", "isAnyOf"].includes(value)
  );
  const dateOperators = getGridDateOperators();

  col.filterable = !(skipColumnsFilterable ?? []).includes(col.field);

  if (
    [
      "featureAgeInDays"
    ].includes(col.field)
  ) {
    return {
      ...col,
      filterOperators: getGridNumericOperators().filter(
        (operator) => !["isAnyOf", "isNotEmpty", "isEmpty"].includes(operator.value)
      )
    };
  } else if (col.field === "createdDate") {
    return {
      ...col,
      filterOperators: dateOperators,
      valueGetter: (value: any, row: { createdDate: string; }) => {
        return row.createdDate ? new Date(row.createdDate) : null;
      }
    };
  } else if (["featureRequestId", "unifiedFeatureRequestId", "title", "description"].includes(col.field)) {
    return {
      ...col,
      filterOperators: stringOperators
    };
  } else {
    return {
      ...col,
      filterOperators: stringOperators.filter(({ value }) => value === "equals")
    };
  }
}

function navigateTo(featureRequestId: string) {
  return `/featureflow?id=${featureRequestId}`;
}

function copyValue(Id: string, setTitle: Dispatch<SetStateAction<string>>) {
  copy(Id).then(() => {
    setTitle(FEATURE_REQUEST_COPY_TITLE);
  });
}

function onMouseEnterTenantId(setTitle: Dispatch<SetStateAction<string>>) {
  setTitle(FEATURE_REQUEST_COPY_TITLE);
}
